import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';

export interface InvoiceLoaderDialogData {
  apiCall: Observable<any>;
  invoiceNumber: string;
  clientName: string;
  clientGstin: string;
  region: string;
  taxableSubledger: number;
  taxAmount: number;
  totalAmount: number;
  type?: 'invoice' | 'quotation' | 'sales_order' | 'proforma';
  isDraft?: boolean;
}

@Component({
  selector: 'app-invoice-loader-dialog',
  templateUrl: './invoice-loader-dialog.component.html',
  styleUrls: ['./invoice-loader-dialog.component.scss']
})
export class InvoiceLoaderDialogComponent implements OnInit, OnDestroy {
  state: 'loading' | 'success' = 'loading';
  currentStep = 0;
  completedSteps: number[] = [];
  progressPct = 0;
  timestamp: string = '';
  ledgerHash: string = '';

  steps: string[] = [];
  loaderTitle = '';
  successTitle = '';
  successSub = '';
  idLabel = '';
  totalLabel = '';
  buttonText = '';

  private apiSubscription: Subscription | null = null;
  private apiCompleted = false;
  private apiSuccess = false;
  private apiError: string | null = null;
  private timerId: any = null;

  constructor(
    public dialogRef: MatDialogRef<InvoiceLoaderDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InvoiceLoaderDialogData
  ) { }

  ngOnInit(): void {
    this.timestamp = new Date().toLocaleString();
    this.ledgerHash = 'cb_block_' + this.generateRandomHash(11);

    const type = this.data.type || 'invoice';
    const isDraft = this.data.isDraft ?? false;

    if (type === 'quotation') {
      this.steps = [
        'Initiating business quotation draft...',
        'Structuring line items & estimated pricing...',
        'Applying tax estimation & discount rates...',
        'Securing quotation price commitment...',
        'Registering quotation in CloudBalance system...'
      ];
      this.loaderTitle = isDraft ? 'Processing quotation draft...' : 'Processing quotation ledger...';
      this.successTitle = isDraft ? 'Quotation successfully saved as draft' : 'Quotation successfully created';
      this.successSub = 'Price estimation and quotation locking is complete.';
      this.idLabel = 'QUOTATION ID';
      this.totalLabel = 'TOTAL';
      this.buttonText = 'Go to quotations';
    } else if (type === 'sales_order') {
      this.steps = [
        'Creating sales order queue entry...',
        'Binding line items and inventory rates...',
        'Calculating taxes & final transaction totals...',
        'Validating credit check and ledger authorization...',
        'Finalizing sales order commit state...'
      ];
      this.loaderTitle = isDraft ? 'Processing sales order draft...' : 'Processing sales order queue...';
      this.successTitle = isDraft ? 'Sales Order successfully saved as draft' : 'Sales Order successfully created';
      this.successSub = 'Sales order verification and backlog allocation complete.';
      this.idLabel = 'SALES ORDER ID';
      this.totalLabel = 'TOTAL';
      this.buttonText = 'Go to sales orders';
    } else if (type === 'proforma') {
      this.steps = [
        'Creating system proforma invoice record...',
        'Appending draft billing line items...',
        'Generating GST compliance entries & tax ledgers...',
        'Applying cryptographic security balance seal...',
        'Syncing proforma invoice to CloudBalance secure cluster...'
      ];
      this.loaderTitle = isDraft ? 'Processing proforma invoice draft...' : 'Processing proforma invoice ledger...';
      this.successTitle = isDraft ? 'Proforma Invoice successfully saved as draft' : 'Proforma Invoice successfully created';
      this.successSub = 'Asynchronous accounting synchronization is complete.';
      this.idLabel = 'PROFORMA INVOICE ID';
      this.totalLabel = 'TOTAL';
      this.buttonText = 'Go to proforma invoices';
    } else {
      this.steps = [
        'Creating system invoice record...',
        'Appending draft billing line items...',
        'Generating GST compliance entries & tax ledgers...',
        'Applying cryptographic security balance seal...',
        'Syncing invoice to CloudBalance secure cluster...'
      ];
      this.loaderTitle = isDraft ? 'Processing invoice draft...' : 'Processing ledger security...';
      this.successTitle = isDraft ? 'Invoice successfully saved as draft' : 'Invoice successfully created';
      this.successSub = 'Asynchronous accounting synchronization is complete.';
      this.idLabel = 'INVOICE ID';
      this.totalLabel = 'TOTAL';
      this.buttonText = 'Go to invoices';
    }

    // Subscribe to parent's API call
    this.apiSubscription = this.data.apiCall.subscribe({
      next: (response: any) => {
        this.apiCompleted = true;
        this.apiSuccess = true;
        // Update details if returned from response
        if (response?.iqp_invoice_number) {
          this.data.invoiceNumber = response.iqp_invoice_number;
        }
        if (response?.iqp_id) {
          this.ledgerHash = 'cb_block_' + response.iqp_id;
        }
        this.checkTransition();
      },
      error: (error: any) => {
        this.apiCompleted = true;
        this.apiSuccess = false;
        const fallbackMsg = type === 'quotation' ? 'Server error occurred while creating quotation' :
          type === 'sales_order' ? 'Server error occurred while creating sales order' :
            type === 'proforma' ? 'Server error occurred while creating proforma invoice' :
              'Server error occurred while creating invoice';
        this.apiError = error?.error?.message || error?.message || fallbackMsg;
        this.checkTransition();
      }
    });

    this.startTicking();
  }

  ngOnDestroy(): void {
    if (this.apiSubscription) {
      this.apiSubscription.unsubscribe();
    }
    if (this.timerId) {
      clearTimeout(this.timerId);
    }
  }

  generateRandomHash(length: number): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  startTicking(): void {
    const tick = () => {
      if (this.currentStep < this.steps.length - 1) {
        this.completedSteps.push(this.currentStep);
        this.currentStep++;
        this.progressPct = Math.floor((this.completedSteps.length / this.steps.length) * 100);
        this.timerId = setTimeout(tick, 1000);
      } else {
        // We are at the final step, wait until API resolves
        this.checkTransition();
      }
    };
    this.timerId = setTimeout(tick, 1000);
  }

  checkTransition(): void {
    if (this.apiCompleted) {
      if (!this.apiSuccess) {
        // API call failed, close dialog and return error to parent
        this.dialogRef.close({ success: false, error: this.apiError });
      } else {
        // API call succeeded. If the animation is on the final step, transition to success
        if (this.currentStep >= this.steps.length - 1) {
          this.completedSteps.push(this.currentStep);
          this.currentStep = this.steps.length;
          this.progressPct = 100;

          setTimeout(() => {
            this.state = 'success';
          }, 600);
        }
      }
    }
  }

  onDoneClick(): void {
    this.dialogRef.close({ success: true });
  }
}
